import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from 'util/UserContext';
import api from 'config/axiosConfig';
import { Info } from 'lucide-react'; // Using lucide-react icons
import Modal from 'ui/Modal'; // Adjust the import path as necessary
import { Issue } from 'util/constants';
import IssueSearch, { Suggestion } from 'components/Issues/IssueSearch';
import InputField from 'ui/InputField';
import { parseWikipediaData } from 'util/wikiUtils';

interface Props {
    open: boolean;
    onClose: (value: boolean) => void;
    prepopulateData?: { name: string; wikipediaLink: string; cname: string; subject_properties: any; } | null;
}

const AddIssue: React.FC<Props> = ({ open, onClose, prepopulateData }) => {
    const { currentUser, setShowAuthModal } = useUser();
    const navigate = useNavigate();
    const [subjectSearchQuery, setSubjectSearchQuery] = useState<string>('');
    const [topicSearchQuery, setTopicSearchQuery] = useState<string>('');
    const [prepopulatedData, setPrepopulatedData] = useState<Props['prepopulateData']>(prepopulateData);
    const [wikipediaLink, setWikipediaLink] = useState<string>(prepopulateData?.wikipediaLink || '');
    const [topicWikipediaLink, setTopicWikipediaLink] = useState<string>('');
    const [selectedSubject, setSelectedSubject] = useState<Suggestion | null>(prepopulateData?.subject_properties ? {
        type: 'wiki',
        name: prepopulateData.name,
        link: prepopulateData.wikipediaLink,
        cname: prepopulateData.cname,
        subject_properties: prepopulateData.subject_properties,
        thumbnail: prepopulateData.subject_properties?.thumbnail?.source || ''
    } : null);
    const [selectedTopic, setSelectedTopic] = useState<Suggestion | null>(null);
    const [error, setError] = useState<string>('');
    const [submitEnabled, setSubmitEnabled] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [isCreatingIssue, setIsCreatingIssue] = useState<boolean>(false);
    const [existingIssueLink, setExistingIssueLink] = useState<string>('');
    const [isChecking, setIsChecking] = useState<boolean>(false);

    // Effect to handle prepopulated data
    useEffect(() => {
        if (open && prepopulateData) {
            setPrepopulatedData(prepopulateData);
            setWikipediaLink(prepopulatedData?.wikipediaLink || '');
            setSelectedSubject({
                type: 'wiki',
                name: prepopulatedData?.name || '',
                link: prepopulatedData?.wikipediaLink || '',
                cname: prepopulatedData?.cname || '',
                subject_properties: prepopulatedData?.subject_properties || {},
                thumbnail: prepopulatedData?.subject_properties?.thumbnail?.source || ''
            });
            setSelectedTopic(null);
        } else {
            // Clear all state when modal is closed
            setPrepopulatedData(null);
            setWikipediaLink('');
            setTopicWikipediaLink('');
            setSelectedSubject(null);
            setSelectedTopic(null);
            setError('');
            setSubmitEnabled(false);
            setIsSubmitting(false);
            setIsCreatingIssue(false);
            setExistingIssueLink('');
            setIsChecking(false);
        }
    }, [open, prepopulateData, prepopulatedData]);

    const validateWikipediaLink = async (link: string, isTopicLink: boolean = false) => {
        setError('');
        const regex = /^https:\/\/en\.wikipedia\.org\/wiki\/[a-zA-Z0-9_().'%,-]+$/;
        if (regex.test(link)) {
            try {
                const title = decodeURIComponent(link.split('/wiki/')[1]);
                const response = await fetch(
                    `https://en.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(title)}`
                );
                const data = await response.json();
                const canonicalTitle = data.titles.canonical;
                setIsChecking(true);
                
                // Check if this exact issue already exists
                const issueResponse = await api.get(`/api/issues?search=${encodeURIComponent(canonicalTitle)}`);
                let existingIssue = issueResponse.data.find(
                    (issue: Issue) => issue.cname === canonicalTitle
                );
                if (existingIssue) {
                    const issueUrl = `/issue/${existingIssue.cname}`;
                    setExistingIssueLink(issueUrl);
                    if (isTopicLink) {
                        setSelectedTopic({...existingIssue, type: 'issue'});
                        setTopicSearchQuery(canonicalTitle.replace(/_/g, ' '));
                    } else {
                        setSelectedSubject({...existingIssue, type: 'issue'});
                        setSubjectSearchQuery(canonicalTitle.replace(/_/g, ' '));
                    }
                } else {           
                    if (isTopicLink) {
                        setTopicWikipediaLink(link);
                        setSelectedTopic({
                            type: 'wiki',
                            name: canonicalTitle,
                            link,
                            cname: canonicalTitle,
                            subject_properties: data,
                            thumbnail: data.thumbnail?.source || ''
                        });
                        setTopicSearchQuery(canonicalTitle.replace(/_/g, ' '));
                    } else {
                        setWikipediaLink(link);
                        setSelectedSubject({
                            type: 'wiki',
                            name: canonicalTitle,
                            link,
                            cname: canonicalTitle,
                            subject_properties: data,
                            thumbnail: data.thumbnail?.source || ''
                        });
                        setSubjectSearchQuery(canonicalTitle.replace(/_/g, ' '));
                    }
                }
            } catch (error) {
                console.error('Error fetching canonical title:', error);
                setError('Error fetching Wikipedia page data');
            } finally {
                setIsChecking(false);
            }
        } else {
            setError('Invalid Wikipedia link. Example: https://en.wikipedia.org/wiki/Crime');
            setIsChecking(false);
        }
    };

    const handleWikipediaLinkChange = async (event: React.ChangeEvent<HTMLInputElement>, isTopicLink: boolean = false) => {
        const value = event.target.value;
        if (isTopicLink) {
            setTopicWikipediaLink(value);
            if (value) await validateWikipediaLink(value, true);
        } else {
            setWikipediaLink(value);
            if (value) await validateWikipediaLink(value);
        }
    };

    const handleSelectSuggestion = (suggestion: Suggestion, isTopic: boolean = false) => {
        if (isTopic) {
            setSelectedTopic(suggestion);
            if (suggestion.type === 'wiki') {
                setTopicWikipediaLink(suggestion.link || '');
            } else {
                setTopicWikipediaLink(suggestion.wikipedia_link || ''); // Clear the link if it's an issue type
            }
        } else {
            setSelectedSubject(suggestion);
            if (suggestion.type === 'wiki') {
                setWikipediaLink(suggestion.link || '');
            } else {
                setWikipediaLink(suggestion.wikipedia_link || ''); // Clear the link if it's an issue type
            }
        }
    };

    useEffect(() => {
        const isValidSubmit = () => {
            setError('');
            // Must have a valid subject and Wikipedia link
            if (!selectedSubject || !isValidWikipediaUrl(wikipediaLink)) return false;

            // If topic exists, both links must be valid
            if (selectedTopic && !isValidWikipediaUrl(topicWikipediaLink)) return false;

            if (selectedSubject.type === 'issue' && !selectedTopic) {
                setError('The subject already exists as an issue; add a topic to create a compound issue.');
                return false;
            }

            // Subject and topic must be different
            if (selectedTopic && selectedTopic.cname === selectedSubject.cname) {
                setError('The subject and topic cannot be the same.');
                return false;
            }

            // Not currently submitting or checking
            if (isSubmitting || isCreatingIssue || isChecking) return false;

            // No existing issue error
            if (error) return false;

            return true;
        };

        setSubmitEnabled(isValidSubmit());
    }, [selectedSubject, selectedTopic, wikipediaLink, topicWikipediaLink, isSubmitting, isCreatingIssue, isChecking, error]);

    // Helper function to validate Wikipedia URLs
    const isValidWikipediaUrl = (url: string) => {
        const regex = /^https:\/\/en\.wikipedia\.org\/wiki\/[a-zA-Z0-9_().'%,-]+$/;
        return regex.test(url);
    };

    const uploadReferenceImage = async (imageUrl: string, issueId: number) => {
        if (!imageUrl) return null;
        
        const imageResponse = await fetch(imageUrl);
      
        let contentType = imageResponse.headers.get('content-type');
  
        if (!contentType) {
          contentType = 'image/jpeg';
        }
        if (contentType.includes('image/svg+xml')) {
          contentType = 'image/svg';
        }
        if (contentType.includes('image/jpeg')) {
          contentType = 'image/jpg';
        }
  
        const blob = await imageResponse.blob();
            
        if (blob) {
            try {
                const formData = new FormData();
                formData.append('image', blob, `reference_image.${contentType?.split('/')[1].replace('jpeg', 'jpg')}`);
                const response = await api.post(`/api/issues/image/${issueId}`, formData);
                return response.data.url;
            } catch (err) {
                console.error('Error uploading reference image:', err);
                return null;
            }
        }
    };

    const handleSearchChange = (isTopic: boolean = false) => {
        if (isTopic) {
            setTopicSearchQuery('');
        } else {
            setSubjectSearchQuery('');
        }
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!currentUser) {
            setShowAuthModal(true);
            return;
        }

        if (!submitEnabled || !selectedSubject) {
            return;
        }

        setIsSubmitting(true);
        setError('');

        try {
            let subjectIssue: Issue | null = null;
            let topicIssue: Issue | null = null;

            // Create or get subject issue
            if (selectedSubject.type === 'wiki') {
                setIsCreatingIssue(true);
                const wikiImg = await parseWikipediaData(selectedSubject.cname, true);
                const subjectResponse = await api.post(`/api/issues`, {
                    wikipedia_link: wikipediaLink,
                    subject_cname: selectedSubject.cname,
                    subject_properties: {
                        ...selectedSubject.subject_properties,
                        wiki_parse_first_img: wikiImg.wiki_parse_first_img
                    },
                    user_id: currentUser.id,
                });
                subjectIssue = subjectResponse.data;
                if (subjectIssue?.properties?.subject_properties?.wiki_parse_first_img) {
                    await uploadReferenceImage(subjectIssue.properties.subject_properties.wiki_parse_first_img, subjectIssue.id);
                }
            } else {
                const response = await api.get(`/api/issues/id/${selectedSubject.id}`);
                subjectIssue = response.data;
            }

            if (!subjectIssue) {
                throw new Error('Failed to create or fetch subject issue');
            }

            // If there's a topic, create or get it
            if (selectedTopic) {
                if (selectedTopic.type === 'wiki') {
                    const topicImg = await parseWikipediaData(selectedTopic.cname, true);
                    const topicResponse = await api.post(`/api/issues`, {
                        wikipedia_link: topicWikipediaLink,
                        subject_cname: selectedTopic.cname,
                        subject_properties: {
                            ...selectedTopic.subject_properties,
                            wiki_parse_first_img: topicImg.wiki_parse_first_img
                        },
                        user_id: currentUser.id,
                    });
                    topicIssue = topicResponse.data;
                    if (topicIssue?.properties?.subject_properties?.wiki_parse_first_img) {
                        await uploadReferenceImage(topicIssue.properties.subject_properties.wiki_parse_first_img, topicIssue.id);
                    }
                } else {
                    const response = await api.get(`/api/issues/id/${selectedTopic.id}`);
                    topicIssue = response.data;
                }

                if (!topicIssue) {
                    throw new Error('Failed to create or fetch topic issue');
                }

                // Create the compound issue
                const compoundResponse = await api.post(`/api/issues`, {
                    wikipedia_link: wikipediaLink,
                    topic_wikipedia_link: topicWikipediaLink,
                    subject_cname: subjectIssue.cname,
                    topic_cname: topicIssue.cname,
                    subject_properties: subjectIssue.properties.subject_properties,
                    topic_properties: topicIssue.properties.subject_properties,
                    user_id: currentUser.id,
                });
                handleCloseModal();
                navigate(`/issue/${compoundResponse.data.cname}?upvoted=true`);
            } else {
                handleCloseModal();
                navigate(`/issue/${subjectIssue.cname}?upvoted=true`);
            }
        } catch (error: any) {
            console.error('Failed to create issue:', error);
            setError(error.response?.data?.message || error.message || 'Failed to create issue. Please try again later.');
        } finally {
            setIsSubmitting(false);
            setIsCreatingIssue(false);
        }
    };

    const handleCloseModal = () => {
        onClose(false);
        setWikipediaLink('');
        setTopicWikipediaLink('');
        setSelectedSubject(null);
        setSelectedTopic(null);
        setError('');
        setSubmitEnabled(false);
        setIsSubmitting(false);
        setIsCreatingIssue(false);
        setExistingIssueLink('');
        setIsChecking(false);
    };

    return (
        <Modal isOpen={open} onClose={handleCloseModal} title="Add issue">
            <div className="flex flex-col h-[calc(100%-44px)] max-h-[calc(100%-44px)] sm:h-auto overflow-y-scroll sm:overflow-y-auto space-y-4">
                <div className="flex flex-col flex-grow space-y-4">
                    <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                        Issues can be anything that is already described on Wikipedia; this helps content stay structured. You can optionally add a second issue as a topic, e.g., Subject: Politics, Topic: Money.
                    </p>

                    <form onSubmit={handleSubmit} className="flex flex-col flex-grow space-y-4">
                        {/* Subject Section */}
                        <div className="relative flex flex-row gap-2 items-center mb-4 mx-2">
                            <div className="w-full border-t border-eoe-border-main dark:border-eoe-dark-border-main" />
                            <div className="mt-0 relative flex flex-row flex-nowrap flex-shrink-0 justify-center text-sm">
                                <h3 className="font-semibold text-eoe-text-heading dark:text-eoe-dark-text-heading">Subject<span className="text-red-500">&nbsp;*</span></h3>
                            </div>
                            <div className="w-full border-t border-eoe-border-main dark:border-eoe-dark-border-main" />
                        </div>

                        <div className="form-control">
                            <IssueSearch
                                inputType="add"
                                placeholder="Subject"
                                className="w-full"
                                autoFocus={false}
                                fieldValue={subjectSearchQuery || prepopulatedData?.name || ''}
                                fieldRequired={true}
                                onSelect={(suggestion) => handleSelectSuggestion(suggestion, false)}
                                onSearchChange={() => handleSearchChange(false)}
                                excludeCompoundIssues={true}
                            />
                        </div>

                        <div className="form-control">
                            <InputField
                                id="wikipedia-link"
                                type="url"
                                name="wikipedia-link"
                                labelText="Wikipedia URL"
                                value={wikipediaLink}
                                onChange={(e) => handleWikipediaLinkChange(e, false)}
                                fieldPlaceholder="https://en.wikipedia.org/wiki/Example"
                                autoComplete="off"
                                spellCheck={false}
                            />
                        </div>

                        {/* Topic Section */}
                        <div className="modal-sub-section">
                            <div className="relative flex flex-row gap-2 items-center mb-4 mx-2">
                                <div className="w-full border-t border-eoe-border-main dark:border-eoe-dark-border-main" />
                                <div className="mt-0 relative flex flex-row flex-nowrap flex-shrink-0 justify-center text-sm">
                                    <h3 className="font-semibold text-eoe-text-heading dark:text-eoe-dark-text-heading">Topic</h3>
                                </div>
                                <div className="w-full border-t border-eoe-border-main dark:border-eoe-dark-border-main" />
                            </div>

                            <div className="form-control">
                                <IssueSearch
                                    inputType="add"
                                    placeholder="Topic (optional)"
                                    className="w-full"
                                    autoFocus={false}
                                    fieldValue={topicSearchQuery || ''}
                                    fieldRequired={false}
                                    onSelect={(suggestion) => handleSelectSuggestion(suggestion, true)}
                                    onSearchChange={() => setSelectedTopic(null)}
                                    excludeCompoundIssues={true}
                                />
                            </div>

                            <div className="form-control">
                                <InputField
                                    id="topic-wikipedia-link"
                                    type="url"
                                    name="topic-wikipedia-link"
                                    labelText="Wikipedia URL"
                                    value={topicWikipediaLink}
                                    onChange={(e) => handleWikipediaLinkChange(e, true)}
                                    fieldPlaceholder="https://en.wikipedia.org/wiki/Different_Example"
                                    autoComplete="off"
                                    spellCheck={false}
                                />
                            </div>
                        </div>

                        {/* Error Message */}
                        {error && (
                            <div className="text-warning-red dark:text-warning-red-dark text-sm flex items-center">
                                <Info className="mr-1 size-4 place-self-start" />
                                {existingIssueLink ? (
                                    <div className="flex flex-col">
                                        <span>{error}</span>
                                        <a href={existingIssueLink} className="internal-link underline hover:no-underline">
                                            View existing issue: {selectedSubject?.name || selectedTopic?.name}
                                        </a>
                                    </div>
                                ) : (
                                    <span>{error}</span>
                                )}
                            </div>
                        )}
                    </form>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-grow flex-col pb-8 justify-end place-items-end mb-6 mt-auto space-y-2 sm:space-y-0 sm:space-x-2 sm:flex sm:justify-end sm:flex-row sm:mb-0">
                    <button
                        type="button"
                        onClick={handleCloseModal}
                        className="btn-secondary w-full sm:w-fit py-2 px-4"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        onClick={handleSubmit}
                        disabled={isSubmitting || !submitEnabled}
                        className={`w-full sm:w-fit py-2 px-4 ${
                            !submitEnabled ? 'btn-primary-disabled' : 'btn-primary'
                        } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        {isSubmitting ? 'Submitting...' : isCreatingIssue ? 'Creating...' : 'Submit'}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default AddIssue;